/**
 * @file src/_modulos/prenota/tabela/layouts/config/content/sort.ts
 * @description Configurações simplificadas de ordenação
 * 
 * Princípios aplicados:
 * - KISS: Configurações simples e diretas
 * - YAGNI: Apenas o que é realmente necessário
 */

import {
  IconArrowUp,
  IconArrowDown,
  IconArrowsUpDown,
} from "@tabler/icons-react";

/* -------------------------------------------------------------------------- */
/* MAPEAMENTO DE CAMPOS                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Mapeia IDs das colunas para campos da API
 */
export const getOrderByField = (columnId: string): string => {
  const fieldMapping: Record<string, string> = {
    F1_XUSRRA: "F1_XUSRRA",
    F1_XTIPO: "F1_XTIPO",
    F1_DOC: "F1_DOC",
    A2_NOME: "A2_NOME",
    Z05_DATA: "Z05_DATA",
    VENCIMENTO: "Z10_VENCTO", // Corrigido: VENCIMENTO mapeia para Z10_VENCTO
    F1_VALBRUT: "F1_VALBRUT",
    F1_XPRIOR: "F1_XPRIOR",
    F1_EMISSAO: "F1_EMISSAO",
    F1_STATUS: "F1_STATUS",
    F1_SERIE: "F1_SERIE",
    F1_DTDIGIT: "F1_DTDIGIT",
    FORNECE: "A2_NOME", // Corrigido: FORNECE mapeia para A2_NOME
    Z05_HORA: "Z05_HORA",
    Status: "Status",
    F1_XORI: "F1_XORI",
    F1_FORNECE: "A2_NOME", // Corrigido: F1_FORNECE mapeia para A2_NOME
  };

  return fieldMapping[columnId] || "Z05_DATA";
};

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS DE ORDENAÇÃO                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Gera parâmetros de ordenação para a API
 */
export const generateSortParams = (columnId: string, desc: boolean) => {
  return {
    order_by: getOrderByField(columnId),
    order_dir: desc ? "DESC" : "ASC",
  };
};

/**
 * Verifica se uma coluna está sendo ordenada
 */
export const isColumnSorted = (
  sorting: Array<{ id: string; desc: boolean }>,
  columnId: string
): boolean => {
  return sorting.some((s) => s.id === columnId);
};

/**
 * Obtém a direção da ordenação atual
 */
export const getCurrentSortDirection = (
  sorting: Array<{ id: string; desc: boolean }>,
  columnId: string
): boolean | null => {
  const currentSort = sorting.find((s) => s.id === columnId);
  return currentSort ? currentSort.desc : null;
};

/* -------------------------------------------------------------------------- */
/* ÍCONES DE ORDENAÇÃO                                                        */
/* -------------------------------------------------------------------------- */

export const sortIcons = {
  unsorted: IconArrowsUpDown,
  ascending: IconArrowUp,
  descending: IconArrowDown,
};

/**
 * Obtém o ícone apropriado para o estado de ordenação
 */
export const getSortIconComponent = (
  sorting: Array<{ id: string; desc: boolean }>,
  columnId: string
) => {
  const isCurrentlySorted = isColumnSorted(sorting, columnId);
  const currentSortDirection = getCurrentSortDirection(sorting, columnId);

  if (!isCurrentlySorted) return sortIcons.unsorted;
  return currentSortDirection ? sortIcons.descending : sortIcons.ascending;
};
